<template>
  <SmTable
    :data="data"
    :columns="computedColumns"
    :loading="loading"
    key-field="id"
    class="h-full"
    @row-click="handleRowClick">
    <template #id="{ row }">
      <TextMultiline :text="row.id" class="font-mono text-sm" />
    </template>

    <template #first_seen="{ row }">
      {{
        getFirstSeen(row) > 0
          ? displayDate(new Date(getFirstSeen(row) * 1000))
          : '-'
      }}
    </template>

    <template #last_seen="{ row }">
      {{
        getLastSeen(row) > 0
          ? displayDate(new Date(getLastSeen(row) * 1000))
          : '-'
      }}
    </template>

    <template #total_sessions="{ row }">
      {{ row.metadata.total_sessions }}
    </template>

    <template #active_days="{ row }">
      {{ row.metadata.active_days }}
    </template>

    <template #no-data>
      <EmptyState
        :icon="Users"
        :title="i18n.t('browserExtension.noUsersFound')"
        :show-create-btn="false"
        :show-docs-btn="false" />
    </template>
  </SmTable>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import { useI18n } from 'vue-i18n'
  import { Users } from 'lucide-vue-next'
  import type { Column } from '@/components/sm/SmTable.types'
  import type { UsageStats } from '@/client'
  import { capitalize } from '@/utils/formatter/formatter'
  import { displayDate } from '@/utils/formatter/date'

  interface Props {
    data: UsageStats[]
    loading?: boolean
    compact?: boolean
    visibleColumns?: Array<
      | keyof UsageStats
      | 'first_seen'
      | 'last_seen'
      | 'total_sessions'
      | 'active_days'
    >
  }

  const props = withDefaults(defineProps<Props>(), {
    loading: false,
    compact: false,
    visibleColumns: undefined,
  })

  const emit = defineEmits<{
    'row-click': [iRow: number, row: UsageStats]
  }>()

  // TODO: why do we need this?
  type VisibleUsersColumn =
    | keyof UsageStats
    | 'first_seen'
    | 'last_seen'
    | 'total_sessions'
    | 'active_days'

  const i18n = useI18n()

  // All available column definitions
  const allColumns: Column<UsageStats>[] = [
    {
      key: 'id',
      label: capitalize(i18n.t('browserExtension.installationId')),
      width: 2,
      sortable: true,
      loader: true,
    },
    {
      key: 'first_seen',
      label: capitalize(i18n.t('browserExtension.firstSeen')),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'last_seen',
      label: capitalize(i18n.t('browserExtension.lastSeen')),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'total_sessions',
      label: capitalize(i18n.t('browserExtension.totalSessions')),
      width: 1,
      sortable: true,
      loader: true,
    },
    {
      key: 'active_days',
      label: capitalize(i18n.t('browserExtension.activeDays')),
      width: 1,
      sortable: true,
      loader: true,
    },
  ]

  // Filter columns based on visibleColumns prop
  const computedColumns = computed<Column<UsageStats>[]>(() => {
    if (!props.visibleColumns || props.visibleColumns.length === 0) {
      // In compact mode, show fewer columns
      if (props.compact) {
        return allColumns.filter((col) =>
          ['id', 'last_seen', 'total_sessions'].includes(col.key)
        )
      }
      return allColumns
    }

    return allColumns.filter((col) => {
      const columnKey = col.key as VisibleUsersColumn
      return props.visibleColumns?.includes(columnKey)
    })
  })

  function getFirstSeen(item: UsageStats): number {
    return item.daily_usages?.[0]?.timestamp || 0
  }

  function getLastSeen(item: UsageStats): number {
    if (!item.daily_usages || item.daily_usages.length === 0) return 0
    return item.daily_usages[item.daily_usages.length - 1]?.timestamp || 0
  }

  function handleRowClick(iRow: number, row: UsageStats) {
    emit('row-click', iRow, row)
  }

  // Expose helper functions for external use if needed
  defineExpose({
    getFirstSeen,
    getLastSeen,
  })
</script>
